/**
 * Renderer Creation — instantiates all visual rendering systems.
 */

import type { Container, Renderer as PixiRendererType } from 'pixi.js';
import type { Player } from '../types';
import { createTankVisuals, type TankVisual } from './tanks';
import { EyeRenderer } from './eyes';
import { ParticleSystem } from './particles';
import { ScreenEffects } from './effects';
import { HUDRenderer } from './hud';
import { RopeRenderer } from './rope';
import { DIRenderer } from './di';
import { BurstRenderer } from './burst';
import { BumperRenderer } from './bumpers';
import { RailgunBeamRenderer } from './railgun-beam';
import { TargetRenderer } from './targets';
import { LotteryRenderer } from './lottery';
import { AnnouncementRenderer } from './announcements';
import { OpponentArrowRenderer } from './opponent-arrow';
import { TerrainRenderer } from '../terrain/rendering';
import { BackgroundRenderer } from './background';
import { WindRenderer } from './wind';
import { VoidRenderer } from './void';
import { PhysicsDebugOverlay } from './debug-physics';
import { NetDebugOverlay } from './debug-net';
import { VortexHoleRenderer } from './vortex-holes';
import { CardDrawRenderer } from './roguelite/card-draw-renderer';
import { RogueliteHUDRenderer } from './roguelite/run-hud';

export interface GameRenderers {
  tankVisuals: TankVisual[];
  eyeRenderer: EyeRenderer;
  particles: ParticleSystem;
  effects: ScreenEffects;
  hud: HUDRenderer;
  ropeRenderer: RopeRenderer;
  diRenderer: DIRenderer;
  burstRenderer: BurstRenderer;
  bumperRenderer: BumperRenderer;
  railgunBeamRenderer: RailgunBeamRenderer;
  targetRenderer: TargetRenderer;
  lotteryRenderer: LotteryRenderer;
  announcements: AnnouncementRenderer;
  opponentArrow: OpponentArrowRenderer;
  terrainRenderer: TerrainRenderer;
  background: BackgroundRenderer;
  windRenderer: WindRenderer;
  voidRenderer: VoidRenderer;
  vortexHoleRenderer: VortexHoleRenderer;
  cardDrawRenderer: CardDrawRenderer;
  rogueliteHUD: RogueliteHUDRenderer;
  debugOverlay: PhysicsDebugOverlay | null;
  netDebugOverlay: NetDebugOverlay | null;
}

export function createRenderers(
  worldContainer: Container,
  uiContainer: Container,
  renderer: PixiRendererType,
  players: Player[],
): GameRenderers {
  const tankVisuals = createTankVisuals(worldContainer, players);
  const eyeRenderer = new EyeRenderer(worldContainer, players);
  const particles = new ParticleSystem(worldContainer, renderer);
  const effects = new ScreenEffects(worldContainer, uiContainer);
  const hud = new HUDRenderer(worldContainer, uiContainer, players.length);
  const ropeRenderer = new RopeRenderer(worldContainer);
  const diRenderer = new DIRenderer(worldContainer, uiContainer, particles);
  const burstRenderer = new BurstRenderer(worldContainer, uiContainer, particles);
  const bumperRenderer = new BumperRenderer(worldContainer);
  const railgunBeamRenderer = new RailgunBeamRenderer(worldContainer, 32);
  const targetRenderer = new TargetRenderer(worldContainer);
  const lotteryRenderer = new LotteryRenderer(worldContainer, uiContainer);
  const announcements = new AnnouncementRenderer(worldContainer, uiContainer);
  const opponentArrow = new OpponentArrowRenderer(uiContainer);

  const terrainRenderer = new TerrainRenderer(worldContainer, renderer);
  terrainRenderer.buildFull();

  const background = new BackgroundRenderer(worldContainer);
  const windRenderer = new WindRenderer(worldContainer);
  const voidRenderer = new VoidRenderer(worldContainer);
  const vortexHoleRenderer = new VortexHoleRenderer(worldContainer);
  const cardDrawRenderer = new CardDrawRenderer(uiContainer);
  const rogueliteHUD = new RogueliteHUDRenderer(uiContainer);

  let debugOverlay: PhysicsDebugOverlay | null = null;
  let netDebugOverlay: NetDebugOverlay | null = null;
  if (import.meta.env.DEV) {
    debugOverlay = new PhysicsDebugOverlay(worldContainer, players.length);
    netDebugOverlay = new NetDebugOverlay(uiContainer);
  }

  return {
    tankVisuals, eyeRenderer, particles, effects, hud, ropeRenderer,
    diRenderer, burstRenderer, bumperRenderer, railgunBeamRenderer,
    targetRenderer, lotteryRenderer, announcements, opponentArrow,
    terrainRenderer, background, windRenderer, voidRenderer,
    vortexHoleRenderer, cardDrawRenderer, rogueliteHUD, debugOverlay,
    netDebugOverlay,
  };
}
